<template>
  <div class="ticket-main-box">
    <!-- 页面进入加载效果 -->
    <transition name="ticket-fade">
      <div v-if="pageLoading" class="ticket-loading-mask">
        <div class="ticket-loading-spinner"></div>
        <p class="ticket-loading-text">{{ $t('加载中...') }}</p>
      </div>
    </transition>

    <LocaleSwitcher class="ticket-locale" />

    <div class="ticket-page__inner">
      <div class="ticket-header">
        <div class="ticket-header__brand">
          <div class="ticket-header__icon">
            <a-icon type="message" />
          </div>
          <span class="ticket-header__label">{{ $t('客户支持') }}</span>
        </div>
        <!-- <router-link to="/login" class="ticket-header__admin">
          <a-icon type="appstore" />
          <span>后台管理</span>
        </router-link> -->
      </div>

      <div class="ticket-title">
        <h1>{{ $t('提交工单') }}</h1>
        <p>{{ $t('请描述您遇到的问题，我们会尽快为您处理。') }}</p>
      </div>

      <div v-if="showProviderInfo" class="ticket-provider">
        <p v-if="agentName" class="ticket-provider__name">
          {{ $t('服务商：') }}{{ agentName }}
        </p>
        <div v-if="serviceContactMobile" class="ticket-provider__phone">
          <a-icon type="phone" />
          <a :href="'tel:' + serviceContactMobile">{{ serviceContactMobile }}</a>
        </div>
      </div>

      <div class="ticket-form">
        <div class="ticket-card ticket-card--contact">
          <div class="ticket-card__header">{{ $t('联系方式') }}</div>
          <div class="ticket-card__body">
            <div class="form-field">
              <label class="form-field__label">
                {{ $t('姓名') }} <span class="required">*</span>
              </label>
              <input
                v-model="form.name"
                class="form-field__input"
                type="text"
                :placeholder="$t('请输入您的姓名')"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">
                {{ $t('手机号码') }} <span class="required">*</span>
              </label>
              <input
                v-model="form.mobile"
                class="form-field__input"
                type="tel"
                maxlength="11"
                :placeholder="$t('请输入手机号码')"
              />
            </div>
          </div>
        </div>

        <div class="ticket-card ticket-card--detail">
          <div class="ticket-card__header">{{ $t('问题详情') }}</div>
          <div class="ticket-card__body">
            <div class="form-field">
              <label class="form-field__label">
                {{ $t('工单标题') }} <span class="required">*</span>
              </label>
              <input
                v-model="form.title"
                class="form-field__input"
                type="text"
                :placeholder="$t('用一句话概括您的问题')"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">
                {{ $t('问题描述') }} <span class="required">*</span>
              </label>
              <textarea
                v-model="form.description"
                class="form-field__textarea"
                :placeholder="$t('请详细描述您遇到的问题，包括发生的时间、操作步骤、错误提示等...')"
              />
            </div>

            <div class="upload-section">
              <a-upload
                v-if="!isMobileDevice"
                class="upload-trigger"
                :file-list="fileList"
                :action="uploadAction"
                :accept="acceptTypes"
                :before-upload="beforeUpload"
                :custom-request="customUpload"
                :show-upload-list="false"
                :multiple="true"
                :disabled="fileList.length >= maxFiles || uploadingCount > 0"
              >
                <div
                  class="upload-box"
                  :class="{ 'upload-box--disabled': fileList.length >= maxFiles }"
                >
                  <div class="upload-box__icon">
                    <a-icon type="upload" />
                  </div>
                  <div class="upload-box__text">
                    <p class="upload-box__title">{{ $t('点击或拖拽上传') }}</p>
                    <p class="upload-box__hint">{{ $t('最多 3 个附件') }}</p>
                  </div>
                </div>
              </a-upload>

              <div
                v-else
                class="upload-picker"
                :class="{ 'upload-picker--disabled': fileList.length >= maxFiles || uploadingCount > 0 }"
                @click="openUploadPicker"
              >
                <div
                  class="upload-box"
                  :class="{ 'upload-box--disabled': fileList.length >= maxFiles }"
                >
                  <div class="upload-box__icon">
                    <a-icon type="upload" />
                  </div>
                  <div class="upload-box__text">
                    <p class="upload-box__title">{{ $t('点击拍照或相册上传') }}</p>
                    <p class="upload-box__hint">{{ $t('最多 3 张图片') }}</p>
                  </div>
                </div>
                <input
                  ref="cameraInput"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  class="upload-picker__input"
                  @change="handleImageSelect"
                />
                <input
                  ref="albumInput"
                  type="file"
                  accept="image/*"
                  multiple
                  class="upload-picker__input"
                  @change="handleImageSelect"
                />
              </div>

              <div
                v-if="isMobileDevice && showUploadSheet"
                class="upload-sheet-mask"
                @click="closeUploadSheet"
              >
                <div class="upload-sheet" @click.stop>
                  <button
                    type="button"
                    class="upload-sheet__item"
                    @click.stop="pickCamera"
                  >
                    {{ $t('拍照') }}
                  </button>
                  <button
                    type="button"
                    class="upload-sheet__item"
                    @click.stop="pickAlbum"
                  >
                    {{ $t('从相册选择') }}
                  </button>
                  <button
                    type="button"
                    class="upload-sheet__cancel"
                    @click.stop="closeUploadSheet"
                  >
                    {{ $t('取消') }}
                  </button>
                </div>
              </div>

              <div v-if="fileList.length" class="file-list">
                <div
                  v-for="file in fileList"
                  :key="file.uid"
                  class="file-item"
                >
                  <div class="file-item__info">
                    <a-icon
                      :type="file.status === 'uploading' ? 'loading' : getFileIcon(file)"
                      class="file-item__clip"
                    />
                    <span class="file-item__name">{{ file.name }}</span>
                    <span v-if="file.status === 'uploading'" class="file-item__status">{{ $t('上传中...') }}</span>
                    <span v-else-if="file.status === 'error'" class="file-item__status file-item__status--error">{{ $t('上传失败') }}</span>
                  </div>
                  <a-icon
                    type="delete"
                    class="file-item__delete"
                    @click.stop="removeFile(file)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="ticket-worktime">{{ $t('工作时间：周一至周五 09:00 - 22:00') }}</p>

      <button
        class="submit-btn"
        :disabled="!canSubmit"
        @click="handleSubmit"
      >
        {{ submitBtnText }}
      </button>
    </div>
  </div>
</template>

<script>
import { t } from "@/i18n";
import LocaleSwitcher from "@/components/LocaleSwitcher.vue";

import axios from "axios";
import qs from "qs";

// 工单接口地址（与代理商后台保持一致）
const AGENT_URL = "https://data.fp1123.com/agent";
const API = {
  submitSupportWork: `${AGENT_URL}/agent/supportWork/submitSupportWork`, // 提交工单
  uploadSupportWorkFile: `${AGENT_URL}/agent/supportWork/uploadSupportWorkFile`, // 上传工单附件
};

// 校验手机号
function validatePhone(s) {
  return /^1[3456789]\d{9}$/.test(s);
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "wmv", "webm", "mkv", "m4v"];
const DOCUMENT_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "txt",
  "csv",
];

const FILE_SIZE_LIMIT = {
  image: 10,
  video: 50,
  document: 20,
};

export default {
  name: "ticketManagement",
  components: {
    LocaleSwitcher
  },
  data() {
    return {
      form: {
        name: "",
        mobile: "",
        title: "",
        description: "",
      },
      pageLoading: true,
      loadTimer: null,
      fileList: [],
      maxFiles: 3,
      uploadingCount: 0,
      uploadAction: API.uploadSupportWorkFile,
      submitting: false,
      showUploadSheet: false,
      uploadPickerSuppressUntil: 0,
      acceptTypes:
        "image/jpeg,image/png,image/gif,image/webp,image/bmp," +
        "video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska," +
        "application/pdf,application/msword," +
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
        "application/vnd.ms-excel," +
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
        "application/vnd.ms-powerpoint," +
        "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
        "text/plain,text/csv,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv",
    };
  },
  computed: {
    isAttachmentUploading() {
      return (
        this.uploadingCount > 0 ||
        this.fileList.some((item) => item.status === "uploading")
      );
    },
    canSubmit() {
      return !this.submitting && !this.isAttachmentUploading;
    },
    submitBtnText() {
      if (this.submitting) return t("提交中...");
      if (this.isAttachmentUploading) return t("附件上传中...");
      return t("提交工单");
    },
    agentName() {
      return this.getAgentName();
    },
    serviceContactMobile() {
      return this.getServiceContactMobile();
    },
    showProviderInfo() {
      return !!(this.agentName || this.serviceContactMobile);
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
  },
  mounted() {
    // 页面进入时的加载效果，短暂展示后淡出
    this.loadTimer = window.setTimeout(() => {
      this.pageLoading = false;
    }, 800);
  },
  beforeDestroy() {
    if (this.loadTimer) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  },
  methods: {
    getFileExtension(fileName) {
      const parts = fileName.split(".");
      return parts.length > 1 ? parts.pop().toLowerCase() : "";
    },
    getFileCategory(file) {
      const ext = this.getFileExtension(file.name);
      if (IMAGE_EXTENSIONS.includes(ext) || (file.type && file.type.startsWith("image/"))) {
        return "image";
      }
      if (VIDEO_EXTENSIONS.includes(ext) || (file.type && file.type.startsWith("video/"))) {
        return "video";
      }
      if (DOCUMENT_EXTENSIONS.includes(ext)) {
        return "document";
      }
      return null;
    },
    getFileIcon(file) {
      const iconMap = {
        image: "file-image",
        video: "video-camera",
        document: "file-text",
      };
      return iconMap[file.category] || "paper-clip";
    },
    validateAttachmentFile(file) {
      const category = this.getFileCategory(file);
      if (!category) {
        this.$message.error(
          this.isMobileDevice
            ? t("仅支持图片格式")
            : t("仅支持图片、视频、文档格式的附件")
        );
        return false;
      }
      const sizeMB = file.size / 1024 / 1024;
      const limit = FILE_SIZE_LIMIT[category];
      if (sizeMB > limit) {
        const typeLabel = {
          image: t("图片"),
          video: t("视频"),
          document: t("文档"),
        }[category];
        this.$message.error(t("{type}大小不能超过 {limit}MB", { type: typeLabel, limit }));
        return false;
      }
      return category;
    },
    beforeUpload(file) {
      if (!this.validateUrlParams()) {
        return false;
      }
      if (this.fileList.length >= this.maxFiles) {
        this.$message.warning(
          t("最多上传 {count} {unit}", {
            count: this.maxFiles,
            unit: this.isMobileDevice ? t("张图片") : t("个附件")
          })
        );
        return false;
      }
      const category = this.validateAttachmentFile(file);
      if (!category) {
        return false;
      }
      const exists = this.fileList.some((item) => item.name === file.name);
      if (exists) {
        this.$message.warning(t("该文件已添加"));
        return false;
      }
      file.category = category;
      return true;
    },
    customUpload({ file, onSuccess, onError }) {
      const category = file.category || this.getFileCategory(file);
      const fileItem = {
        uid: file.uid,
        name: file.name,
        category,
        status: "uploading",
        originFileObj: file,
      };
      this.fileList = [...this.fileList, fileItem];
      this.uploadingCount += 1;
      this.uploadSupportWorkFile(file)
        .then((fileInfo) => {
          const index = this.fileList.findIndex((item) => item.uid === file.uid);
          if (index >= 0) {
            const nextList = [...this.fileList];
            nextList.splice(index, 1, {
              ...fileItem,
              status: "done",
              url: fileInfo.fileUrl,
              fileName: fileInfo.fileName,
              fileType: fileInfo.fileType,
            });
            this.fileList = nextList;
          }
          onSuccess({ data: fileInfo }, file);
        })
        .catch((error) => {
          const index = this.fileList.findIndex((item) => item.uid === file.uid);
          if (index >= 0) {
            const nextList = [...this.fileList];
            nextList.splice(index, 1, {
              ...fileItem,
              status: "error",
            });
            this.fileList = nextList;
          }
          onError(error);
          this.$message.error(error.message || t("附件上传失败"));
        })
        .finally(() => {
          this.uploadingCount -= 1;
        });
    },
    removeFile(file) {
      this.fileList = this.fileList.filter((item) => item.uid !== file.uid);
    },
    isUploadPickerSuppressed() {
      return Date.now() < this.uploadPickerSuppressUntil;
    },
    suppressUploadPicker(ms = 800) {
      this.uploadPickerSuppressUntil = Date.now() + ms;
    },
    openUploadPicker() {
      if (this.isUploadPickerSuppressed()) return;
      if (this.fileList.length >= this.maxFiles || this.uploadingCount > 0) {
        if (this.fileList.length >= this.maxFiles) {
          this.$message.warning(
            t("最多上传 {count} {unit}", {
              count: this.maxFiles,
              unit: t("张图片")
            })
          );
        }
        return;
      }
      if (!this.validateUrlParams()) return;
      this.showUploadSheet = true;
    },
    closeUploadSheet() {
      this.showUploadSheet = false;
    },
    triggerImageInput(refName) {
      this.closeUploadSheet();
      this.suppressUploadPicker();
      window.setTimeout(() => {
        const input = this.$refs[refName];
        if (input) {
          input.click();
        }
      }, 100);
    },
    pickCamera() {
      this.triggerImageInput("cameraInput");
    },
    pickAlbum() {
      this.triggerImageInput("albumInput");
    },
    normalizeImageFile(rawFile, source, index) {
      const ext = this.getFileExtension(rawFile.name) || "jpg";
      const needRename = source === "camera" || !rawFile.name;
      const fileName = needRename
        ? `photo_${Date.now()}_${index}.${ext}`
        : rawFile.name;
      const file = needRename
        ? new File([rawFile], fileName, {
            type: rawFile.type || "image/jpeg",
            lastModified: rawFile.lastModified,
          })
        : rawFile;
      file.uid = `${source}-${Date.now()}-${index}`;
      return file;
    },
    handleImageSelect(event) {
      this.closeUploadSheet();
      this.suppressUploadPicker();
      const input = event.target;
      const source = input === this.$refs.cameraInput ? "camera" : "album";
      const rawFiles = input.files ? Array.from(input.files) : [];
      input.value = "";
      if (!rawFiles.length) return;

      rawFiles.forEach((rawFile, index) => {
        if (this.fileList.length >= this.maxFiles) {
          this.$message.warning(
            t("最多上传 {count} {unit}", {
              count: this.maxFiles,
              unit: t("张图片")
            })
          );
          return;
        }
        const file = this.normalizeImageFile(rawFile, source, index);
        if (!this.beforeUpload(file)) return;
        this.customUpload({
          file,
          onSuccess() {},
          onError() {},
        });
      });
    },
    getUrlQueryParam(key) {
      const routeVal = this.$route.query[key];
      if (routeVal != null && routeVal !== "") {
        return String(routeVal);
      }
      const hash = window.location.hash || "";
      const queryIndex = hash.indexOf("?");
      if (queryIndex !== -1) {
        const hashVal = new URLSearchParams(hash.slice(queryIndex + 1)).get(key);
        if (hashVal != null && hashVal !== "") {
          return String(hashVal);
        }
      }
      const searchVal = new URLSearchParams(window.location.search || "").get(key);
      if (searchVal != null && searchVal !== "") {
        return String(searchVal);
      }
      return "";
    },
    getCompanyId() {
      return this.getUrlQueryParam("companyId");
    },
    getStoreId() {
      return this.getUrlQueryParam("storeId");
    },
    getUserId() {
      return this.getUrlQueryParam("userId");
    },
    getAgentName() {
      return this.getUrlQueryParam("agentName");
    },
    getServiceContactMobile() {
      return this.getUrlQueryParam("contactMobile");
    },
    validateUrlParams() {
      if (!this.getCompanyId()) {
        this.$message.error(t("缺少企业 ID 参数，请通过正确链接访问"));
        return false;
      }
      if (!this.getStoreId()) {
        this.$message.error(t("缺少门店 ID 参数，请通过正确链接访问"));
        return false;
      }
      if (!this.getUserId()) {
        this.$message.error(t("缺少用户 ID 参数，请通过正确链接访问"));
        return false;
      }
      return true;
    },
    validateForm() {
      if (!this.validateUrlParams()) {
        return false;
      }
      const { name, mobile, title, description } = this.form;
      if (!name.trim()) {
        this.$message.warning(t("请输入您的姓名"));
        return false;
      }
      if (!mobile.trim()) {
        this.$message.warning(t("请输入手机号码"));
        return false;
      }
      if (!validatePhone(mobile)) {
        this.$message.warning(t("请输入正确的手机号码"));
        return false;
      }
      if (!title.trim()) {
        this.$message.warning(t("请输入工单标题"));
        return false;
      }
      if (!description.trim()) {
        this.$message.warning(t("请输入问题描述"));
        return false;
      }
      return true;
    },
    async uploadSupportWorkFile(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("companyId", this.getCompanyId());
      formData.append("storeId", this.getStoreId());
      const res = await axios.post(API.uploadSupportWorkFile, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (res.data && res.data.status == 200 && res.data.data && res.data.data.fileUrl) {
        return res.data.data;
      }
      throw new Error((res.data && res.data.msg) || t("附件上传失败"));
    },
    getUploadedFileUrls() {
      return this.fileList
        .filter((item) => item.status === "done" && item.url)
        .map((item) => item.url)
        .join(",");
    },
    resetForm() {
      this.form = {
        name: "",
        mobile: "",
        title: "",
        description: "",
      };
      this.fileList = [];
    },
    async handleSubmit() {
      if (!this.canSubmit) return;
      if (!this.validateForm()) return;
      this.submitting = true;
      try {
        const fileUrls = this.getUploadedFileUrls();
        const params = {
          title: this.form.title.trim(),
          content: this.form.description.trim(),
          contactName: this.form.name.trim(),
          contactMobile: this.form.mobile.trim(),
          fileUrls,
          companyId: this.getCompanyId(),
          storeId: this.getStoreId(),
          submitUserId: this.getUserId(),
          querySource: 3,
        };
        const res = await axios.post(
          API.submitSupportWork,
          qs.stringify(params)
        );
        if (res.data.status == 200) {
          this.$message.success(
            res.data.msg || t("工单提交成功，我们会尽快为您处理")
          );
          this.resetForm();
        } else {
          this.$message.error(res.data.msg || t("提交失败，请稍后重试"));
        }
      } catch (error) {
        this.$message.error(error.message || t("提交失败，请稍后重试"));
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
input,
textarea {
  background: none;
  outline: none;
  border: 0;
  min-width: 0;
  font-family: inherit;
}

@bg-color: #e6f5f2;
@primary: #2a9d8f;
@text-primary: #1a3530;
@text-secondary: #4a7a72;
@placeholder: #9ec4be;
@input-bg: #f5fefc;
@input-border: #d8ede9;
@card-border: #e0f0ec;
@card-header-border: #f0f8f6;
@upload-border: #c8e8e2;
@link-color: #1890ff;
@file-bg: #fafafa;
@shadow-card: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
@shadow-btn: 0 4px 3px rgba(42, 157, 143, 0.3);
@shadow-icon: 0 2px 2px rgba(42, 157, 143, 0.3);

// 旧版 Android WebView（Chrome < 84）不支持 flex 的 gap，统一用相邻兄弟外边距模拟间距
.flex-gap-col(@size) {
  > * + * {
    margin-top: @size;
  }
}

.flex-gap-row(@size) {
  > * + * {
    margin-left: @size;
  }
}

.ticket-locale {
  position: fixed;
  top: 20px;
  right: 24px;
  z-index: 30;
}

.ticket-main-box {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #fff;
  box-sizing: border-box;
  padding: 40px 16px;

  .ticket-page__inner {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 40px 32px;
    background: @bg-color;
    border-radius: 32px;
    display: flex;
    flex-direction: column;
    .flex-gap-col(24px);
    box-sizing: border-box;
  }
}

// 页面进入加载遮罩与转圈动画
.ticket-loading-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1001;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ticket-loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e0f0ec;
  border-top-color: @primary;
  border-radius: 50%;
  animation: ticket-spin 0.8s linear infinite;
}

.ticket-loading-text {
  margin: 16px 0 0;
  font-size: 14px;
  color: @text-secondary;
}

@keyframes ticket-spin {
  to {
    transform: rotate(360deg);
  }
}

.ticket-fade-leave-active {
  transition: opacity 0.4s;
}

.ticket-fade-leave-to {
  opacity: 0;
}

.ticket-header {
  display: none;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  &__brand {
    display: flex;
    align-items: center;
    .flex-gap-row(12px);
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: @primary;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    box-shadow: @shadow-icon;
  }

  &__label {
    font-size: 12px;
    font-weight: 700;
    color: @primary;
    letter-spacing: 1.2px;
    text-transform: uppercase;
  }

  &__admin {
    display: flex;
    align-items: center;
    .flex-gap-row(6px);
    color: @text-secondary;
    font-size: 14px;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: @primary;
    }
  }
}

.ticket-title {
  display: flex;
  flex-direction: column;
  .flex-gap-col(12px);

  // 移动端隐藏了 .ticket-header，标题作为首个可见元素不再额外带顶部间距
  .ticket-main-box .ticket-page__inner > & {
    margin-top: 0;
  }

  h1 {
    margin: 0;
    font-size: 32px;
    font-weight: 500;
    line-height: 40px;
    color: @text-primary;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 23px;
    color: @text-secondary;
  }
}

.ticket-provider {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  // 换行场景由子元素外边距实现 24px 横向间距 + 8px 行间距，末尾用负边距抵消多出的行间距
  margin-bottom: -8px;

  &__name {
    margin: 0 24px 8px 0;
    font-size: 16px;
    font-weight: 500;
    color: @text-primary;
  }

  &__phone {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    .flex-gap-row(8px);
    font-size: 18px;
    font-weight: 700;
    color: @text-primary;
    white-space: nowrap;

    .anticon {
      font-size: 22px;
      flex-shrink: 0;
    }

    a {
      color: @text-primary;
      text-decoration: none;
      font-weight: 700;

      &:hover {
        color: @primary;
      }
    }
  }
}

.ticket-worktime {
  margin: 0;
  width: 100%;
  font-size: 16px;
  line-height: 1.5;
  color: @text-primary;
  text-align: center;
}

.ticket-form {
  display: flex;
  flex-direction: column;
  .flex-gap-col(24px);
  align-items: stretch;
  width: 100%;
}

.ticket-card {
  background: #fff;
  border: 1px solid @card-border;
  border-radius: 24px;
  box-shadow: @shadow-card;
  // 不能用 overflow: hidden，否则旧 Android WebView 会把 fixed 定位的上传方式弹层一起裁掉
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;

  &--contact {
    width: 100%;
  }

  &--detail {
    flex: 1;
    min-width: 0;
  }

  &__header {
    height: 48px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid @card-header-border;
    font-size: 18px;
    font-weight: 700;
    color: @primary;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }

  &__body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    .flex-gap-col(24px);
    flex: 1;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  .flex-gap-col(8px);

  &__label {
    font-size: 14px;
    font-weight: 700;
    color: @text-primary;
    line-height: 1;

    .required {
      color: @primary;
    }
  }

  &__input,
  &__textarea {
    width: 100%;
    box-sizing: border-box;
    background: @input-bg;
    border: 1px solid @input-border;
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 14px;
    color: @text-primary;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: inherit;

    &::placeholder {
      color: @placeholder;
    }

    &:focus {
      border-color: @primary;
      box-shadow: 0 0 0 2px rgba(42, 157, 143, 0.15);
    }
  }

  &__input {
    height: 48px;
  }

  &__textarea {
    min-height: 120px;
    resize: vertical;
    line-height: 1.5;
  }
}

.upload-section {
  display: flex;
  flex-direction: column;
  .flex-gap-col(16px);
  align-items: stretch;
  width: 100%;

  // 上传方式弹层为 fixed 定位，不参与间距计算
  .upload-sheet-mask {
    margin: 0;
  }
}

.upload-trigger {
  width: 100%;

  /deep/ .ant-upload {
    display: block;
    width: 100%;
  }

  /deep/ .ant-upload-select {
    display: block;
    width: 100%;
  }

  /deep/ .ant-upload-select-picture {
    display: block;
    width: 100%;
  }
}

.upload-picker {
  position: relative;
  width: 100%;
  cursor: pointer;

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }

  &__input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }
}

.upload-sheet-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.upload-sheet {
  width: 100%;
  max-width: 480px;
  padding: 0 12px 24px;
  box-sizing: border-box;

  &__item,
  &__cancel {
    display: block;
    width: 100%;
    border: none;
    background: #fff;
    font-size: 16px;
    line-height: 24px;
    padding: 16px;
    cursor: pointer;
  }

  &__item {
    color: @text-primary;
    border-bottom: 1px solid #f0f0f0;

    &:first-child {
      border-radius: 12px 12px 0 0;
    }

    &:last-of-type {
      border-bottom: none;
      border-radius: 0 0 12px 12px;
    }

    &:only-of-type {
      border-radius: 12px;
    }
  }

  &__cancel {
    margin-top: 8px;
    border-radius: 12px;
    color: @text-secondary;
  }
}

.upload-box {
  width: 100%;
  min-height: 120px;
  box-sizing: border-box;
  border: 2px dashed @upload-border;
  border-radius: 12px;
  background: @input-bg;
  display: flex;
  align-items: center;
  justify-content: center;
  .flex-gap-row(8px);
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover:not(.upload-box--disabled) {
    border-color: @primary;
    background: #eef9f7;
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background: #f0faf8;
    color: @primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    .flex-gap-col(4px);
    // 旧 WebView 下允许文字收缩，避免被图标压住或溢出到框外
    flex: 0 1 auto;
    min-width: 0;
    text-align: center;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: @text-primary;
    line-height: 20px;
  }

  &__hint {
    margin: 0;
    font-size: 12px;
    color: @text-secondary;
    line-height: 16px;
    text-align: center;
  }
}

.file-list {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 80px;
}

.file-item {
  display: flex;
  align-items: center;
  .flex-gap-row(8px);
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: @file-bg;

    .file-item__delete {
      opacity: 1;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    .flex-gap-row(8px);
  }

  &__clip {
    color: @text-secondary;
    font-size: 14px;
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    color: @link-color;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__status {
    flex-shrink: 0;
    font-size: 12px;
    color: @text-secondary;

    &--error {
      color: #ff4d4f;
    }
  }

  &__delete {
    font-size: 14px;
    color: @text-secondary;
    cursor: pointer;
    opacity: 0;
    padding: 3px 5px;
    transition: opacity 0.2s, color 0.2s;
    flex-shrink: 0;

    &:hover {
      color: #ff4d4f;
    }
  }
}

.submit-btn {
  width: 100%;
  height: 64px;
  border: none;
  border-radius: 80px;
  background: @primary;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.4px;
  cursor: pointer;
  box-shadow: @shadow-btn;
  transition: background 0.2s, opacity 0.2s;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: darken(@primary, 5%);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

/* 移动端 / 平板：< 1200px，对应 Figma 6322:31055 */
@media (max-width: 1199px) {
  .ticket-title {
    .flex-gap-col(16px);

    h1 {
      font-size: 48px;
      line-height: 1.2;
      font-weight: 500;
    }

    p {
      font-size: 24px;
      line-height: 1.4;
    }
  }

  .ticket-provider {
    &__name {
      margin-right: 16px;
      font-size: 28px;
      font-weight: 500;
    }

    &__phone {
      font-size: 32px;
      font-weight: 700;

      .anticon {
        font-size: 36px;
      }

      a {
        font-weight: 700;
      }
    }
  }

  .ticket-worktime {
    font-size: 24px;
  }

  .ticket-card__header {
    height: 80px;
    padding: 0 20px;
    font-size: 32px;
  }

  .form-field {
    .flex-gap-col(16px);

    &__label {
      font-size: 30px;
    }

    &__input {
      height: 96px;
      padding: 12px 24px;
      font-size: 16px;
      border-radius: 16px;
    }

    &__textarea {
      min-height: 320px;
      padding: 24px;
      font-size: 16px;
      border-radius: 12px;
    }
  }

  .upload-box {
    min-height: 136px;
    .flex-gap-row(24px);

    &__icon {
      width: 64px;
      height: 64px;
      background: @primary;
      color: #fff;
      font-size: 32px;
    }

    &__title {
      font-size: 30px;
      line-height: 1.3;
    }

    &__hint {
      font-size: 24px;
      line-height: 1.3;
    }
  }

  .file-list {
    min-height: auto;
    .flex-gap-col(16px);
    justify-content: flex-start;
  }

  .file-item {
    padding: 8px;
    .flex-gap-row(16px);
    background: @file-bg;

    &__clip {
      font-size: 20px;
    }

    &__name {
      font-size: 16px;
    }

    &__delete {
      opacity: 1;
      font-size: 20px;
      padding: 6px 10px;
    }
  }

  .submit-btn {
    height: 96px;
    font-size: 32px;
    font-weight: 400;
  }
}

/* 平板适中字号：768px ~ 1199px */
@media (min-width: 768px) and (max-width: 1199px) {
  .ticket-main-box {
    padding: 40px 24px;

    .ticket-page__inner {
      max-width: 720px;
    }
  }

  .ticket-title {
    h1 {
      font-size: 40px;
    }

    p {
      font-size: 18px;
    }
  }

  .ticket-provider {
    &__name {
      font-size: 16px;
    }

    &__phone {
      font-size: 18px;
      font-weight: 700;

      .anticon {
        font-size: 22px;
      }
    }
  }

  .ticket-worktime {
    font-size: 14px;
  }

  .ticket-card__header {
    height: 64px;
    font-size: 24px;
  }

  .form-field {
    .flex-gap-col(12px);

    &__label {
      font-size: 16px;
    }

    &__input {
      height: 56px;
      padding: 12px 16px;
    }

    &__textarea {
      min-height: 200px;
      padding: 16px;
    }
  }

  .upload-box {
    min-height: 120px;
    .flex-gap-row(16px);

    &__icon {
      width: 56px;
      height: 56px;
      font-size: 28px;
    }

    &__title {
      font-size: 16px;
    }

    &__hint {
      font-size: 14px;
    }
  }

  .submit-btn {
    height: 72px;
    font-size: 20px;
    font-weight: 700;
  }
}

/* 小屏手机：≤ 767px */
@media (max-width: 767px) {
  .ticket-locale {
    top: 12px;
    right: 12px;
  }

  .ticket-main-box {
    padding: 16px 12px;

    .ticket-page__inner {
      padding: 32px 20px;
      .flex-gap-col(20px);
    }
  }

  .ticket-title {
    .flex-gap-col(12px);

    h1 {
      font-size: 32px;
    }

    p {
      font-size: 16px;
    }
  }

  .ticket-provider {
    &__name {
      margin-right: 12px;
      font-size: 14px;
    }

    &__phone {
      font-size: 16px;
      font-weight: 700;

      .anticon {
        font-size: 18px;
      }
    }
  }

  .ticket-worktime {
    font-size: 12px;
  }

  .ticket-card__header {
    height: 56px;
    font-size: 20px;
  }

  .ticket-card__body {
    padding: 20px;
    .flex-gap-col(20px);
  }

  .form-field {
    .flex-gap-col(8px);

    &__label {
      font-size: 14px;
    }

    &__input {
      height: 48px;
      padding: 12px 16px;
      border-radius: 12px;
    }

    &__textarea {
      min-height: 160px;
      padding: 16px;
    }
  }

  .upload-box {
    min-height: 100px;
    .flex-gap-row(16px);

    &__icon {
      width: 48px;
      height: 48px;
      font-size: 24px;
    }

    &__title {
      font-size: 14px;
    }

    &__hint {
      font-size: 12px;
    }
  }

  .file-item {
    &__clip,
    &__delete {
      font-size: 16px;
    }

    &__name {
      font-size: 14px;
    }
  }

  .submit-btn {
    height: 56px;
    font-size: 18px;
    font-weight: 700;
  }
}

/* 桌面端：≥ 1200px，对应 Figma 6257:30435 */
@media (min-width: 1200px) {
  .ticket-main-box {
    padding: 40px;

    .ticket-page__inner {
      max-width: 1200px;
      padding: 40px 80px;
      .flex-gap-col(16px);
    }
  }

  .ticket-header {
    display: flex;
  }

  // 桌面端显示顶栏，标题需要与顶栏保持间距
  .ticket-main-box .ticket-page__inner > .ticket-title {
    margin-top: 16px;
  }

  .ticket-title {
    .flex-gap-col(12px);

    h1 {
      font-size: 32px;
      line-height: 40px;
    }

    p {
      font-size: 14px;
      line-height: 23px;
    }
  }

  .ticket-form {
    flex-direction: row;

    > * + * {
      margin-top: 0;
      margin-left: 16px;
    }
  }

  .ticket-card--contact {
    width: 480px;
    flex-shrink: 0;
  }

  .ticket-card--detail .ticket-card__body {
    .flex-gap-col(16px);
  }

  .upload-section {
    flex-direction: row;
    align-items: flex-start;

    > * + * {
      margin-top: 0;
      margin-left: 16px;
    }
  }

  .upload-trigger,
  .upload-picker {
    width: 200px;
    flex-shrink: 0;
  }

  .upload-box {
    width: 200px;
    min-height: 80px;
    flex-direction: row;
    .flex-gap-row(8px);
    padding: 0 16px;

    &__icon {
      width: 48px;
      height: 48px;
      background: #f0faf8;
      color: @primary;
      font-size: 24px;
    }

    &__text {
      align-items: center;
      text-align: center;
      .flex-gap-col(8px);
    }

    &__title {
      font-size: 14px;
      line-height: 20px;
      white-space: nowrap;
    }

    &__hint {
      font-size: 12px;
      line-height: 16px;
      white-space: nowrap;
      text-align: center;
    }
  }

  .file-list {
    flex: 1;
    min-width: 0;
    min-height: 80px;
    justify-content: space-between;
  }

  .file-item__delete {
    opacity: 0;
  }

  .submit-btn {
    height: 64px;
    font-size: 18px;
    font-weight: 700;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ticketManagement/ticketManagement.vue